import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { exchangeStepUpPassword } from '@/store/actions/stepUpAction'
import { Tokens } from '@/constants'
import { setSecureItem, removeSecureItem } from '@/lib/secureStorage'

/**
 * W107 (§18) · Step-up authentication.
 *
 * A gated action answers 428 STEP_UP_REQUIRED when the organisation requires a
 * fresh proof of presence. The API client hands that response here; this
 * dialog asks the person for their password, exchanges it at POST
 * /auth/step-up for a short-lived proof, stores the proof so the client can
 * attach it as X-Step-Up, and lets the client retry the action. The password
 * is sent to the platform's own endpoint and kept nowhere.
 */
const StepUpDialog = () => {
  const dispatch = useDispatch()
  const [challenge, setChallenge] = useState(null)
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const resolver = useRef(null)

  useEffect(() => {
    window.__acclimetryStepUp = (payload) => new Promise((resolve) => {
      resolver.current = resolve
      setPassword('')
      setError(null)
      setChallenge(payload || {})
    })
    return () => { delete window.__acclimetryStepUp }
  }, [])

  const finish = (token) => {
    const resolve = resolver.current
    resolver.current = null
    setChallenge(null)
    setPassword('')
    if (resolve) resolve(token)
  }

  const confirm = async (event) => {
    event.preventDefault()
    if (!password) return
    setBusy(true)
    setError(null)
    try {
      const res = await dispatch(exchangeStepUpPassword({ password }))
      const token = res?.data?.data?.token || null
      if (token) setSecureItem(Tokens.STEP_UP_TOKEN, token)
      finish(token)
    } catch (err) {
      removeSecureItem(Tokens.STEP_UP_TOKEN)
      setError(err?.response?.data?.message || 'That password was not recognised.')
    } finally {
      setBusy(false)
    }
  }

  if (!challenge) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="step-up-title">
      <form onSubmit={confirm} className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl flex flex-col gap-3">
        <h2 id="step-up-title" className="text-base font-semibold text-slate-900">Confirm it is you</h2>
        <p className="text-sm text-gray-600">
          {challenge.message || 'This action needs a fresh proof that you are present. Confirm your password to continue.'}
          {challenge.ttlMinutes ? ` The proof lasts ${challenge.ttlMinutes} minute(s).` : ''}
        </p>
        <div>
          <Label htmlFor="step-up-password" className="text-xs">Password</Label>
          <Input id="step-up-password" type="password" autoComplete="current-password" autoFocus value={password} onChange={(event) => setPassword(event.target.value)} />
        </div>
        {error ? <p className="text-sm text-red-700" role="alert">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => finish(null)}>Cancel</Button>
          <Button type="submit" size="sm" disabled={busy || !password}>Confirm</Button>
        </div>
      </form>
    </div>
  )
}

export default StepUpDialog
